import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, updateDoc, setDoc, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../config/firebase';

function FamilyInvitations() {
  const { currentUser } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);   // inbound: people wanting to join MY families
  const [myRequests, setMyRequests] = useState([]);       // outbound: requests I sent to other families
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests'); // start on the most actionable tab
  const [requestsNeedsIndex, setRequestsNeedsIndex] = useState(false);
  const [invitesNeedsIndex, setInvitesNeedsIndex] = useState(false);

  useEffect(() => {
    async function fetchAll() {
      if (!currentUser) return;
      setLoading(true);
      try {
        // 1. Email invitations addressed to this user
        const inviteSnap = await getDocs(query(
          collectionGroup(db, 'invitations'),
          where('invitedUserEmail', '==', currentUser.email),
          where('status', '==', 'pending')
        ));
        const userInvitations = await Promise.all(
          inviteSnap.docs.map(async d => {
            const inv = { id: d.id, familyId: d.ref.parent.parent.id, ...d.data() };
            if (!inv.familyName) {
              try {
                const fSnap = await getDoc(doc(db, 'families', inv.familyId));
                inv.familyName = fSnap.data()?.name || 'a family';
              } catch {}
            }
            return inv;
          })
        );
        setInvitations(userInvitations);

        // 2. Inbound join requests to families I admin
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const userFamilyIds = userDoc.data()?.familyIds || [];
        const inbound = [];
        for (const familyId of userFamilyIds) {
          try {
            const familyDoc = await getDoc(doc(db, 'families', familyId));
            const familyData = familyDoc.data();
            const isAdmin = familyData.createdBy === currentUser.uid ||
              (familyData.adminIDs || []).includes(currentUser.uid);
            if (isAdmin) {
              const snap = await getDocs(query(
                collection(db, 'families', familyId, 'requests'),
                where('status', '==', 'pending')
              ));
              snap.forEach(d => inbound.push({ id: d.id, familyId, familyName: familyData.name, ...d.data() }));
            }
          } catch (e) {
            console.error('Error fetching requests for family', familyId, e);
          }
        }
        setJoinRequests(inbound);

        // 3. Outbound: join requests I sent to other families.
        // Primary path: per-family queries via pendingJoinFamilyIds (always reliable).
        // Supplement with collectionGroup in case there are requests not yet tracked.
        {
          const seen = new Set();
          const out = [];

          // Primary: per-family queries
          try {
            const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
            const pendingFamilyIds = userSnap.data()?.pendingJoinFamilyIds || [];
            for (const fid of pendingFamilyIds) {
              try {
                const snap = await getDocs(
                  query(collection(db, 'families', fid, 'requests'), where('userId', '==', currentUser.uid))
                );
                snap.docs.forEach(d => {
                  if (!seen.has(d.id)) { seen.add(d.id); out.push({ id: d.id, familyId: fid, ...d.data() }); }
                });
              } catch (_) { /* skip this family */ }
            }
          } catch (e) {
            console.warn('My requests: per-family fetch failed', e);
          }

          // Supplement: collectionGroup (catches requests predating pendingJoinFamilyIds)
          try {
            const outSnap = await getDocs(query(
              collectionGroup(db, 'requests'),
              where('userId', '==', currentUser.uid)
            ));
            outSnap.docs.forEach(d => {
              if (!seen.has(d.id)) { seen.add(d.id); out.push({ id: d.id, familyId: d.ref.parent.parent.id, ...d.data() }); }
            });
          } catch (e) {
            if (e?.code !== 'failed-precondition' && !/index/i.test(e?.message || '')) {
              console.warn('My requests: collectionGroup failed', e);
            }
          }

          out.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
          setMyRequests(out);
        }
      } catch (e) {
        console.error('Error fetching invitations and requests:', e);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [currentUser]);

  const handleAcceptInvitation = async (invitation) => {
    try {
      await updateDoc(doc(db, 'families', invitation.familyId), {
        memberIDs: arrayUnion(currentUser.uid),
        [`memberPermissions.${currentUser.uid}`]: { canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true },
      });
      await updateDoc(doc(db, 'users', currentUser.uid), { familyIds: arrayUnion(invitation.familyId) });
      await setDoc(doc(db, 'families', invitation.familyId, 'members', currentUser.uid), {
        userId: currentUser.uid, userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        photoURL: currentUser.photoURL || '', bio: '',
        joinedAt: new Date().toISOString(), role: 'member',
        canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true,
      });
      await updateDoc(doc(db, 'families', invitation.familyId, 'invitations', invitation.id), {
        status: 'accepted', acceptedAt: new Date().toISOString(), acceptedBy: currentUser.uid,
      });
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      alert(`You've joined ${invitation.familyName || 'the family'}!`);
    } catch (e) {
      console.error('Error accepting invitation:', e);
      alert('Failed to accept invitation. Please try again.');
    }
  };

  const handleDeclineInvitation = async (invitation) => {
    try {
      await deleteDoc(doc(db, 'families', invitation.familyId, 'invitations', invitation.id));
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
    } catch (e) {
      console.error('Error declining invitation:', e);
      alert('Failed to decline invitation. Please try again.');
    }
  };

  const handleApproveRequest = async (request) => {
    try {
      await updateDoc(doc(db, 'families', request.familyId), { memberIDs: arrayUnion(request.userId) });
      await updateDoc(doc(db, 'users', request.userId), { familyIds: arrayUnion(request.familyId) });
      await setDoc(doc(db, 'families', request.familyId, 'members', request.userId), {
        userId: request.userId, userEmail: request.userEmail, displayName: request.displayName,
        photoURL: '', bio: '', joinedAt: new Date().toISOString(), role: 'member',
        canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true,
      });
      await updateDoc(doc(db, 'families', request.familyId, 'requests', request.id), { status: 'approved' });
      setJoinRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (e) {
      console.error('Error approving request:', e);
      alert('Failed to approve request. Please try again.');
    }
  };

  const handleDenyRequest = async (request) => {
    try {
      await updateDoc(doc(db, 'families', request.familyId, 'requests', request.id), { status: 'denied' });
      setJoinRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (e) {
      console.error('Error denying request:', e);
      alert('Failed to deny request. Please try again.');
    }
  };

  const handleCancelRequest = async (request) => {
    if (!window.confirm(`Cancel your request to join ${request.familyName}?`)) return;
    try {
      await deleteDoc(doc(db, 'families', request.familyId, 'requests', request.id));
      await updateDoc(doc(db, 'users', currentUser.uid), {
        pendingJoinFamilyIds: arrayRemove(request.familyId),
      });
      setMyRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (e) {
      console.error('Error cancelling request:', e);
      alert('Failed to cancel request. Please try again.');
    }
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Approved</span>;
    if (status === 'denied')   return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Denied</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-white/50">Pending</span>;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'requests', label: 'To My Family', count: joinRequests.length },
    { id: 'invitations', label: 'Invitations', count: invitations.length },
    { id: 'my-requests', label: 'My Requests', count: myRequests.filter(r => r.status === 'pending').length },
  ];

  return (
    <div className="card">
      <div className="border-b border-gray-200 dark:border-white/10 mb-6">
        <nav className="-mb-px flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-cerulean'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white/70 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Inbound: requests to join my family ── */}
      {activeTab === 'requests' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Requests to Join My Family</h3>
          {joinRequests.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No pending join requests.</p>
          ) : (
            <div className="space-y-3">
              {joinRequests.map(request => (
                <div key={request.id} className="border border-gray-200 dark:border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {request.displayName || request.userEmail} wants to join {request.familyName}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-white/50">{request.userEmail}</p>
                      {request.createdAt && (
                        <p className="text-xs text-gray-400 mt-0.5">{new Date(request.createdAt).toLocaleDateString()}</p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      <button onClick={() => handleApproveRequest(request)} className="btn-primary text-sm">Approve</button>
                      <button onClick={() => handleDenyRequest(request)} className="btn-secondary text-sm">Deny</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Inbound: email invitations addressed to me ── */}
      {activeTab === 'invitations' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Invitations Received</h3>
          {invitations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No pending invitations.</p>
          ) : (
            <div className="space-y-3">
              {invitations.map(invitation => (
                <div key={invitation.id} className="border border-gray-200 dark:border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Invitation to join {invitation.familyName}
                      </h4>
                      {invitation.invitedByName && (
                        <p className="text-sm text-gray-500 dark:text-white/50">from {invitation.invitedByName}</p>
                      )}
                      {invitation.createdAt && (
                        <p className="text-xs text-gray-400 mt-0.5">{new Date(invitation.createdAt).toLocaleDateString()}</p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      <button onClick={() => handleAcceptInvitation(invitation)} className="btn-primary text-sm">Accept</button>
                      <button onClick={() => handleDeclineInvitation(invitation)} className="btn-secondary text-sm">Decline</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Outbound: join requests I sent ── */}
      {activeTab === 'my-requests' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">My Join Requests</h3>
          {requestsNeedsIndex ? (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-500/10 dark:border-yellow-500/40 px-4 py-3 text-sm text-yellow-800 dark:text-yellow-200">
              Requests are temporarily unavailable — a database index is still building. Check back in a minute and refresh.
            </div>
          ) : myRequests.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No join requests sent.</p>
          ) : (
            <div className="space-y-3">
              {myRequests.map(request => (
                <div key={request.id} className="border border-gray-200 dark:border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{request.familyName}</h4>
                      {request.createdAt && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Requested {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                      {statusBadge(request.status)}
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleCancelRequest(request)}
                          className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FamilyInvitations;
